import { motion } from "framer-motion";
import { Mail, Phone, ArrowUpRight } from "lucide-react";
import { FOUNDERS, founderName } from "../data/content";
import { useLang } from "../i18n/LanguageContext";

const Contact = () => {
  const { t, lang } = useLang();
  const C = t.contact;
  const roles = t.team.roles;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-navy text-white relative overflow-hidden"
    >
      <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-coral/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-20 w-[360px] h-[360px] rounded-full bg-white/5 blur-3xl pointer-events-none" />

      <div className="container-vel relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div
              className="overline mb-5"
              style={{ color: "#FF6B35" }}
              data-testid="contact-overline"
            >
              {C.overline}
            </div>
            <h2
              data-testid="contact-heading"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-white"
            >
              {C.headline}
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5 font-body text-base sm:text-lg text-white/70 leading-relaxed font-light"
          >
            {C.intro}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {FOUNDERS.map((person) => {
            const name = founderName(person, lang);
            return (
              <motion.div
                key={person.id}
                data-testid={`contact-card-${person.id}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6 }}
                className="bg-navy p-10 md:p-12 group"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-body text-[10px] tracking-[0.22em] uppercase text-coral">
                      {roles[person.id]}
                    </div>
                    <h3 className="mt-3 font-heading text-3xl sm:text-4xl font-medium text-white tracking-tight">
                      {name}
                    </h3>
                  </div>
                  <ArrowUpRight
                    size={22}
                    strokeWidth={1.25}
                    className="text-white/30 group-hover:text-coral group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-500"
                  />
                </div>

                <div className="mt-10 space-y-5">
                  <a
                    data-testid={`contact-email-${person.id}`}
                    href={`mailto:${person.email}`}
                    className="flex items-center gap-4 group/link"
                  >
                    <span className="flex items-center justify-center w-10 h-10 border border-white/20 group-hover/link:border-coral group-hover/link:bg-coral transition-all duration-300">
                      <Mail size={16} strokeWidth={1.5} />
                    </span>
                    <div>
                      <div className="font-body text-[10px] tracking-[0.22em] uppercase text-white/50">
                        {C.emailLabel}
                      </div>
                      <div className="font-body text-base text-white group-hover/link:text-coral transition-colors duration-300">
                        {person.email}
                      </div>
                    </div>
                  </a>

                  <a
                    data-testid={`contact-phone-${person.id}`}
                    href={`tel:${person.phoneHref}`}
                    className="flex items-center gap-4 group/link"
                  >
                    <span className="flex items-center justify-center w-10 h-10 border border-white/20 group-hover/link:border-coral group-hover/link:bg-coral transition-all duration-300">
                      <Phone size={16} strokeWidth={1.5} />
                    </span>
                    <div>
                      <div className="font-body text-[10px] tracking-[0.22em] uppercase text-white/50">
                        {C.phoneLabel}
                      </div>
                      <div className="font-body text-base text-white group-hover/link:text-coral transition-colors duration-300">
                        {person.phone}
                      </div>
                    </div>
                  </a>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div
          data-testid="contact-footer-note"
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-white/10 pt-8"
        >
          <p className="font-body text-sm text-white/60">{C.footerOffice}</p>
          <p className="font-body text-xs tracking-[0.22em] uppercase text-white/40">
            {C.footerNote}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Contact;
